import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  InputError,
  SwapMode,
  SwapProvider,
  SwapStep,
  SwapTokenPair,
} from '../interfaces/swapInterface'
import ICPSwap from '../clients/ICPSwap'
import KongSwap from '../clients/KongSwap'
import { SWAP_COMMISSION, SWAP_COMMISSION_MEMO, SWAP_COMMISSION_TOTAL } from '../constants/business'
import { useCurrentWallet, useSwapSlippage } from '../state'
import { subaccountFromPrincipal } from '../utils/subaccount'
import { decimalsToE8s } from '../utils/validationHelper'
import getAgent from '../utils/getAgent'
import { logger } from '../logger'
import { transferToken, approveToken, getIdentity } from '../handlers/walletActions/wallet'
import { HttpAgent } from '@dfinity/agent'
import { Principal } from '@dfinity/principal'
import useConfig from './useConfig'
import useBalances from './useBalances'
import { DEFAULT_SWAP_TOKENS, useSwapContext } from '../providers/SwapProvider'
import { executeAndSetInterval } from '../utils/executeAndSetInterval'
import { formatNumber } from '../utils/formatNumber'
import { useTranslation } from 'react-i18next'
import OdinClient from '../clients/OdinClient'
import { Chain, PlugToken } from '../interfaces/common'
import { transferPlugCommission } from '../utils/transferPlugCommission'
import { asICToken, asOdinToken } from '../utils/castToken'
import { ICP_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { MIN_ODIN_SWAP_AMOUNT } from './useBridgeOdin'

const KONG_BACKEND_CANISTER_ID = '2ipq2-uqaaa-aaaar-qailq-cai'
const PRICE_REFRESH_INTERVAL = 10000

interface Props {
  fromTokenId?: string
  toTokenId?: string
  fromValue?: string
}

export interface SwapPairPrice {
  pair: SwapTokenPair
  price: number
  humanPrice: number
  slippage?: number
}

const useSwap = ({ fromTokenId, toTokenId, fromValue }: Props) => {
  const [allPairs, setAllPairs] = useState<SwapTokenPair[]>([])
  const [keyringAgent, setKeyringAgent] = useState<HttpAgent>()
  const [loadingPairs, setLoadingPairs] = useState(false)
  const [matchedPairs, setMatchedPairs] = useState<SwapTokenPair[]>([])
  const [currentSwapStep, setCurrentSwapStep] = useState<SwapStep>()
  const [priceLoading, setPriceLoading] = useState(false)
  const [swapExecutionError, setExecutionSwapError] = useState<string>()

  const { slippage } = useSwapSlippage()
  const { currentWallet: principal } = useCurrentWallet()

  const {
    manualProvider,
    quotes,
    setQuotes,
    amount,
    setSelectedProvider,
    setToAmount,
    setFromToken,
    setToToken,
    fromToken,
    toToken,
    setAmount,
  } = useSwapContext()

  const { tokens } = useConfig()
  const { findBalance } = useBalances()
  const { t } = useTranslation()

  const tokenFee = fromToken?.fee || 0

  const kongSwapService = useMemo(() => {
    if (!keyringAgent) return

    return new KongSwap(keyringAgent)
  }, [keyringAgent])

  const icpSwapService = useMemo(() => {
    if (!keyringAgent) return

    return new ICPSwap(keyringAgent)
  }, [keyringAgent])

  const odinService = useMemo(() => {
    if (!keyringAgent) return

    return new OdinClient(keyringAgent)
  }, [keyringAgent])

  const currentQuote = useMemo(() => {
    if (manualProvider) return quotes.find((q) => q.pair.provider === manualProvider)

    return quotes.sort((a, b) => b.price - a.price)[0]
  }, [quotes, manualProvider])

  const getTokenById = (id: string | undefined) =>
    id ? tokens.find((token) => token.id === id) : undefined

  useEffect(() => {
    let fToken = getTokenById(fromTokenId)
    if (!fToken) {
      fToken = getTokenById(ICP_ICP_ID)
    }

    let tToken = getTokenById(toTokenId)
    if (!tToken && fToken) {
      const defaults = DEFAULT_SWAP_TOKENS[fToken.chain]
      tToken = getTokenById(fToken.id === defaults?.from ? defaults?.to : defaults?.from)
    }

    setFromToken(fToken)
    setToToken(tToken)
  }, [fromTokenId, toTokenId])

  useEffect(() => {
    if (fromValue) setAmount(fromValue)
  }, [fromValue])

  useEffect(() => {
    if (!principal) return

    getIdentity(principal)
      .then((identity) => {
        const agent = getAgent({ identity })

        setKeyringAgent(agent)
      })
      .catch(logger.debug)
  }, [principal])

  useEffect(() => {
    if (!kongSwapService) return

    kongSwapService
      .getPairs()
      .then(setAllPairs)
      .catch((e) => logger.error('Failed to get Kong pairs', { originalError: e }))
  }, [kongSwapService])

  const findPairs = async (fromToken: PlugToken, toToken: PlugToken): Promise<SwapTokenPair[]> => {
    const fetches: Promise<SwapTokenPair>[] = []

    if (fromToken.chain === Chain.ICP) {
      const from = asICToken(fromToken)
      const to = asICToken(toToken)

      if (icpSwapService) fetches.push(icpSwapService.getPair(from, to))
      if (kongSwapService) fetches.push(kongSwapService.getPair(from, to))
    }

    if (fromToken.chain === Chain.ODIN) {
      const from = asOdinToken(fromToken)
      const to = asOdinToken(toToken)

      if (odinService) fetches.push(odinService.getPair(from, to))
    }

    const results = await Promise.allSettled(fetches)

    return results.filter((result) => result.status === 'fulfilled').map((result) => result.value)
  }

  useEffect(() => {
    setMatchedPairs([])
  }, [fromToken?.address, toToken?.address])

  useEffect(() => {
    if (!fromToken || !toToken) return setMatchedPairs([])

    setLoadingPairs(true)
    findPairs(fromToken, toToken)
      .then(setMatchedPairs)
      .catch((e) => {
        logger.error('Failed to find swap pairs', { originalError: e })
        setMatchedPairs([])
      })
      .finally(() => setLoadingPairs(false))
  }, [fromToken?.address, toToken?.address, icpSwapService, kongSwapService, odinService])

  const fetchQuote = useCallback(
    async (amount: string, swapPair: SwapTokenPair) => {
      if (!kongSwapService || !icpSwapService || !odinService)
        return Promise.reject('No Dex service available')

      if (!fromToken || !toToken) return Promise.reject('Token configs unavailable')

      const pairMode =
        fromToken?.address === swapPair.token0.principal ? SwapMode.SELL : SwapMode.BUY

      const normalizedAmount = Number(decimalsToE8s(amount, fromToken.decimals))

      const swappableAmount = Math.floor(normalizedAmount * (1 - SWAP_COMMISSION_TOTAL))

      let swapInfo

      switch (swapPair.provider) {
        case SwapProvider.KONGSWAP:
          swapInfo = await kongSwapService.getQuoteKong(
            pairMode,
            swappableAmount,
            asICToken(fromToken).address,
            asICToken(toToken).address
          )
          break
        case SwapProvider.ODIN:
          swapInfo = await odinService.getQuoteOdin(
            asOdinToken(fromToken),
            asOdinToken(toToken),
            swappableAmount
          )
          break
        default:
          swapInfo = await icpSwapService.getQuote(
            swapPair.swapCanisterPrincipal,
            pairMode,
            swappableAmount
          )
      }

      return {
        price: swapInfo.quote,
        humanPrice: swapInfo.quote * 10 ** -toToken.decimals,
        slippage: swapInfo.slippage,
      }
    },
    [kongSwapService, icpSwapService, odinService, fromToken?.address, toToken?.address]
  )

  const getQuotes = useCallback(
    async (amount: string): Promise<SwapPairPrice[]> => {
      if (!Number(amount)) return []

      const prices = Promise.all(
        matchedPairs.map(async (pair) => {
          const { price, humanPrice, slippage } = await fetchQuote(amount, pair)
          return { pair, price, humanPrice, slippage }
        })
      )

      return prices
    },
    [fetchQuote, JSON.stringify(matchedPairs)]
  )

  useEffect(() => {
    let interval: NodeJS.Timeout | number
    const timeout = setTimeout(() => {
      interval = executeAndSetInterval(() => {
        setPriceLoading(true)

        getQuotes(amount)
          .then(setQuotes)
          .catch((e) => logger.error('Unable to fetch quotes', { originalError: e }))
          .finally(() => setPriceLoading(false))
      }, PRICE_REFRESH_INTERVAL)
    }, 300)

    return () => {
      setPriceLoading(false)
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [getQuotes, amount])

  useEffect(() => {
    setSelectedProvider(currentQuote?.pair.provider)
    setToAmount(formatNumber(currentQuote?.humanPrice, '', { notation: 'standard' }) || '')
  }, [currentQuote, toToken?.decimals])

  const getDepositAddress = useCallback(
    (pair: SwapTokenPair) => {
      if (!kongSwapService) return Promise.reject('No Dex service available')

      return kongSwapService.getDepositAddress(
        pair.swapCanisterPrincipal,
        Principal.fromText(principal!)
      )
    },
    [kongSwapService, principal]
  )

  const priceHuman = useMemo(() => {
    if (fromToken && toToken && currentQuote && amount) {
      const price = currentQuote.humanPrice / parseFloat(amount)

      return `1 ${fromToken.symbol} = ${formatNumber(price, toToken.symbol)}`
    }
  }, [fromToken, toToken, currentQuote, amount])

  const performSwap = useCallback(
    async (pair: SwapTokenPair, quote: number) => {
      if (!fromToken || !toToken) return Promise.reject('No token selected')
      if (!kongSwapService || !icpSwapService || !odinService)
        return Promise.reject('No Dex service available')

      const fromAmount = Number(decimalsToE8s(amount, fromToken.decimals))
      const priceLimit = Math.ceil(quote * (1 - slippage / 100))

      let plugCommission = 0

      if (
        [SwapProvider.ICPSWAP, SwapProvider.KONGSWAP, SwapProvider.ODIN].includes(pair.provider)
      ) {
        plugCommission = Math.floor(fromAmount * SWAP_COMMISSION)

        transferPlugCommission(plugCommission, fromToken, SWAP_COMMISSION_MEMO)
      }

      const swapAmount = fromAmount - plugCommission
      const swapMode = fromToken.address === pair.token0.principal ? SwapMode.SELL : SwapMode.BUY

      switch (pair.provider) {
        case SwapProvider.ICPSWAP:
          setCurrentSwapStep(SwapStep.Transfer)

          if (fromToken.standard === 'ICRC1') {
            await transferToken({
              receiver: {
                address: pair.swapCanisterPrincipal,
                subaccount: Array.from(subaccountFromPrincipal(principal)),
              },
              amount: swapAmount.toString(),
              standard: fromToken.standard,
              tokenCanisterId: asICToken(fromToken).address,
            })
          } else {
            setCurrentSwapStep(SwapStep.Approve)
            await approveToken({
              standard: fromToken.standard,
              tokenCanisterId: asICToken(fromToken).address,
              spender: {
                address: pair.swapCanisterPrincipal,
              },
              amount: (swapAmount + tokenFee).toString(),
            })
          }

          return icpSwapService.swap(
            pair.swapCanisterPrincipal,
            swapMode,
            asICToken(fromToken),
            asICToken(toToken),
            swapAmount,
            priceLimit,
            slippage,
            setCurrentSwapStep
          )
        case SwapProvider.KONGSWAP:
          let transferResponse: any = undefined

          if (['ICRC1'].includes(fromToken.standard)) {
            setCurrentSwapStep(SwapStep.Transfer)
            transferResponse = await transferToken({
              standard: fromToken.standard,
              tokenCanisterId: asICToken(fromToken).address,
              receiver: {
                address: KONG_BACKEND_CANISTER_ID,
                subaccount: undefined,
              },
              amount: swapAmount.toString(),
            })
          } else {
            setCurrentSwapStep(SwapStep.Approve)
            await approveToken({
              standard: fromToken.standard,
              tokenCanisterId: asICToken(fromToken).address,
              spender: {
                address: KONG_BACKEND_CANISTER_ID,
              },
              amount: (swapAmount + tokenFee).toString(),
            })
          }

          setCurrentSwapStep(SwapStep.Swap)

          return kongSwapService.swap(
            transferResponse,
            swapMode,
            asICToken(fromToken),
            asICToken(toToken),
            swapAmount,
            priceLimit,
            slippage,
            setCurrentSwapStep
          )
        default:
          setCurrentSwapStep(SwapStep.Swap)

          return odinService.swap(
            '',
            swapMode,
            asOdinToken(fromToken),
            asOdinToken(toToken),
            swapAmount,
            priceLimit,
            slippage,
            setCurrentSwapStep
          )
      }
    },
    [
      getDepositAddress,
      fromToken,
      kongSwapService,
      toToken?.decimals,
      fromToken?.decimals,
      fromToken?.fee,
      toToken?.fee,
      toToken,
      principal,
      fromToken?.standard,
      slippage,
      amount,
    ]
  )

  const getSwapHistory = useCallback(
    async (principal: string) => {
      if (!kongSwapService || !principal) return []

      const pairs = await kongSwapService.getPairs()
      const events = await Promise.all(
        pairs.map((pair) =>
          kongSwapService.getSwapPairEvents(pair.swapCanisterPrincipal, principal)
        )
      )

      return events.flat()
    },
    [kongSwapService]
  )

  const fallback = useCallback(
    (subaccount: number[]) => {
      const selectedPair = currentQuote?.pair

      if (!selectedPair || !kongSwapService) return Promise.reject('Swap pair not selected')
      return kongSwapService.fallback(selectedPair?.swapCanisterPrincipal, subaccount)
    },
    [kongSwapService, currentQuote]
  )

  const swapError: InputError | undefined = useMemo(() => {
    if (!fromToken || !toToken) {
      return {
        buttonError: t('swap.errors.no_token'),
      }
    }

    if (!Number(amount))
      return {
        buttonError: t('swap.errors.no_amount'),
      }

    if (loadingPairs) {
      return {
        buttonError: t('swap.errors.searching_pairs'),
      }
    }

    if (priceLoading) {
      return {
        buttonError: t('swap.errors.loading_price'),
      }
    }

    if (!currentQuote) {
      return {
        buttonError: t('swap.errors.no_pair'),
      }
    }

    const numericAmount = Number(amount)
    const fromTokenFee = (fromToken.fee || 0) * 10 ** -fromToken.decimals * 2
    const toTokenFee = (toToken.fee || 0) * 10 ** -toToken.decimals * 2

    if (numericAmount <= fromTokenFee || currentQuote.humanPrice <= toTokenFee) {
      return {
        buttonError: t('swap.errors.less_than_minimal'),
      }
    }

    if (fromToken.chain === Chain.ODIN) {
      const compareAmount = fromToken.id === ODINBTC_ID ? numericAmount : currentQuote.humanPrice

      if (compareAmount < MIN_ODIN_SWAP_AMOUNT) {
        return {
          buttonError: t('swap.errors.less_than_minimal'),
        }
      }
    }

    const fromTokenBalance = findBalance(fromToken)?.balance

    if (!fromTokenBalance || numericAmount > fromTokenBalance) {
      return {
        buttonError: t('swap.errors.not_enough_token', {
          token: fromToken.name,
        }),
      }
    }

    if (numericAmount + fromTokenFee - 10 ** -fromToken.decimals > fromTokenBalance) {
      return {
        buttonError: t('swap.errors.not_enough_token_for_fee', {
          token: fromToken.name,
        }),
      }
    }
  }, [
    fromToken,
    toToken,
    amount,
    currentQuote,
    fromToken?.decimals,
    fromToken?.name,
    loadingPairs,
    quotes,
    priceLoading,
  ])

  const fromTokenBalance = findBalance(fromToken)?.balance || 0
  const maxAmount = useMemo(() => {
    if (!fromToken) return 0

    const fromTokenFee = (fromToken.fee || 0) * 10 ** -fromToken.decimals * 2

    return Math.max(0, fromTokenBalance - fromTokenFee)
  }, [fromToken?.id, fromTokenBalance])

  return {
    getDepositAddress,
    performSwap,
    getSwapHistory,
    allPairs,
    fallback,
    loadingPairs,
    currentSwapStep,
    priceLoading,
    currentQuote,
    swapError,
    swapExecutionError,
    setExecutionSwapError,
    priceHuman,
    getQuotes,
    maxAmount,
  }
}

export default useSwap
